import React, { useEffect, useRef } from 'react';
import usePrefersReducedMotion from './usePrefersReducedMotion';

// How far outside the viewport a clip starts loading, so it isn't blank on arrival.
const MARGIN_PX = 300;

// Decorative loops that sit below the fold. `preload="none"` means the browser
// fetches nothing until play() is called, so opening the page no longer pulls
// several MB of video the visitor hasn't scrolled to yet. Pausing again on the
// way out keeps idle decoding off the CPU, which matters on weaker machines.
function LazyVideo({ src, poster, className, width, height, onClick, onError }) {
  const ref = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Nothing should start moving on its own if motion is unwelcome.
    if (reducedMotion) {
      if (!el.paused) el.pause();
      return undefined;
    }

    let frame = 0;

    // Idempotent: every trigger below just re-asks "should this be playing?".
    const sync = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const near =
        rect.bottom > -MARGIN_PX && rect.top < window.innerHeight + MARGIN_PX;

      if (near) {
        // play() rejects when a browser blocks autoplay; nothing to recover
        // from, the frame just stays where it is.
        if (el.paused) el.play().catch(() => {});
      } else if (!el.paused) {
        el.pause();
      }
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    // Scroll/resize is the path that always works. IntersectionObserver is
    // added on top purely so idle scrolling doesn't run this per frame.
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    let observer;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(sync, {
        rootMargin: `${MARGIN_PX}px 0px`,
      });
      observer.observe(el);
    }

    sync();
    // Late-loading images above can still move this element; re-check once the
    // page has fully settled.
    window.addEventListener('load', schedule);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
      if (observer) observer.disconnect();
    };
  }, [reducedMotion]);

  return (
    <video
      ref={ref}
      poster={poster}
      className={className}
      // Reserves the clip's aspect ratio so nothing jumps as it loads.
      width={width}
      height={height}
      loop
      muted
      playsInline
      preload="none"
      onClick={onClick}
      onError={onError}
    >
      <source src={src} type="video/webm" />
    </video>
  );
}

export default LazyVideo;
